import type { SuperAdminOrganizationSummary } from "@multi-chat/shared";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge } from "../../components/ui/badge/index.js";
import { Button } from "../../components/ui/button/index.js";
import { ProductErrorState } from "../../components/common/ProductErrorState.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { formatStorageGigabyteAmount } from "../settings/format-storage-gigabytes.js";
import type { SuperAdminLoadState } from "./SuperAdminDashboardPage.js";
import { PlatformEntitlementMark } from "./PlatformEntitlementMark.js";
import { SuperAdminCursorPaginationBar } from "./SuperAdminCursorPaginationBar.js";
import {
  contactsTableHeaderLabelClass,
  superAdminTableBodyCellClass,
  superAdminTableBodyRowClass,
  superAdminTableClass,
  superAdminTableHeaderCellClass,
  superAdminTableHeadRowClass,
  superAdminTableScrollClass,
  superAdminTableWorkspaceClass,
  type SuperAdminTablePageSize,
} from "./super-admin-table-presentation.js";

function limitText(
  limit: number | string | null,
  unlimited: string,
  format: (value: number | string) => string,
): string {
  return limit === null ? unlimited : format(limit);
}

function HeaderCell({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <th className={superAdminTableHeaderCellClass}>
      <span className={contactsTableHeaderLabelClass}>{children}</span>
    </th>
  );
}

export function SuperAdminOrganizationsView({
  state,
  search,
  pageSize,
  isFirstPage,
  onSearchChange,
  onSearchSubmit,
  onPageSizeChange,
  onNextPage,
  onFirstPage,
  onRetry,
}: {
  state: SuperAdminLoadState<{ items: SuperAdminOrganizationSummary[]; nextCursor: string | null }>;
  search: string;
  pageSize: SuperAdminTablePageSize;
  isFirstPage: boolean;
  onSearchChange: (value: string) => void;
  onSearchSubmit: () => void;
  onPageSizeChange: (size: SuperAdminTablePageSize) => void;
  onNextPage: () => void;
  onFirstPage: () => void;
  onRetry: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const unlimited = t("settings.usage.unlimited");
  const numberFormat = new Intl.NumberFormat(i18n.language);
  const ready = state.status === "ready";
  const items = ready ? state.data.items : [];
  const nextCursor = ready ? state.data.nextCursor : null;

  return (
    <div className="grid gap-4">
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          onSearchSubmit();
        }}
      >
        <label className="sr-only" htmlFor="super-admin-org-search">
          {t("superAdmin.organizations.searchLabel")}
        </label>
        <input
          id="super-admin-org-search"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={t("superAdmin.organizations.searchPlaceholder")}
          className="h-9 w-full max-w-sm rounded-md border border-line/70 bg-surface px-3 text-sm text-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.55)]"
          data-testid="super-admin-org-search"
        />
        <Button type="submit" size="sm" variant="outline">
          {t("superAdmin.organizations.search")}
        </Button>
      </form>

      {state.status === "loading" ? (
        <div
          className={`${superAdminTableWorkspaceClass} h-40 animate-pulse`}
          data-testid="super-admin-org-loading"
          aria-busy="true"
        />
      ) : null}

      {state.status === "error" ? (
        <ProductErrorState
          testId="super-admin-org-error"
          title={t("superAdmin.organizations.loadErrorTitle")}
          description={t("superAdmin.organizations.loadErrorBody")}
          onRetry={onRetry}
          retryLabel={t("superAdmin.retry")}
        />
      ) : null}

      {ready && items.length === 0 ? (
        <p className="m-0 text-sm text-muted" data-testid="super-admin-org-empty">
          {t("superAdmin.organizations.empty")}
        </p>
      ) : null}

      {ready && items.length > 0 ? (
        <div className={superAdminTableWorkspaceClass}>
          <div className={superAdminTableScrollClass}>
            <table className={`${superAdminTableClass} min-w-[52rem]`} data-testid="super-admin-org-table">
              <thead>
                <tr className={superAdminTableHeadRowClass}>
                  <HeaderCell>{t("superAdmin.organizations.columns.organization")}</HeaderCell>
                  <HeaderCell>{t("superAdmin.organizations.columns.status")}</HeaderCell>
                  <HeaderCell>{t("superAdmin.organizations.columns.contacts")}</HeaderCell>
                  <HeaderCell>{t("superAdmin.organizations.columns.team")}</HeaderCell>
                  <HeaderCell>{t("superAdmin.organizations.columns.storage")}</HeaderCell>
                  <HeaderCell>{t("superAdmin.organizations.columns.platforms")}</HeaderCell>
                  <HeaderCell>{t("superAdmin.organizations.columns.created")}</HeaderCell>
                </tr>
              </thead>
              <tbody>
                {items.map((organization) => (
                  <tr
                    key={organization.id}
                    className={superAdminTableBodyRowClass}
                    data-testid={`super-admin-org-row-${organization.id}`}
                  >
                    <td className={superAdminTableBodyCellClass}>
                      <Link
                        to={`/super-admin/organizations/${organization.id}`}
                        className="font-medium text-foreground no-underline hover:underline"
                        data-testid={`super-admin-org-link-${organization.id}`}
                      >
                        {organization.name}
                      </Link>
                    </td>
                    <td className={superAdminTableBodyCellClass}>
                      <Badge variant={organization.status === "ACTIVE" ? "success" : "warning"}>
                        {t(`superAdmin.status.${organization.status}`)}
                      </Badge>
                    </td>
                    <td className={superAdminTableBodyCellClass}>
                      <LtrValue>
                        {t("settings.usage.usedOfLimit", {
                          used: numberFormat.format(organization.usage.contacts.used),
                          limit: limitText(organization.usage.contacts.limit, unlimited, (value) =>
                            numberFormat.format(Number(value)),
                          ),
                        })}
                      </LtrValue>
                    </td>
                    <td className={superAdminTableBodyCellClass}>
                      <LtrValue>
                        {t("settings.usage.usedOfLimit", {
                          used: numberFormat.format(organization.usage.team.occupied),
                          limit: limitText(organization.usage.team.limit, unlimited, (value) =>
                            numberFormat.format(Number(value)),
                          ),
                        })}
                      </LtrValue>
                    </td>
                    <td className={superAdminTableBodyCellClass}>
                      <LtrValue>
                        {t("settings.usage.usedOfLimit", {
                          used: t("settings.usage.gigabytes", {
                            amount: formatStorageGigabyteAmount(BigInt(organization.usage.storage.usedBytes)),
                          }),
                          limit:
                            organization.usage.storage.limitBytes === null
                              ? unlimited
                              : t("settings.usage.gigabytes", {
                                  amount: formatStorageGigabyteAmount(
                                    BigInt(organization.usage.storage.limitBytes),
                                  ),
                                }),
                        })}
                      </LtrValue>
                    </td>
                    <td className={superAdminTableBodyCellClass}>
                      <span className="flex flex-wrap items-center gap-2.5">
                        <PlatformEntitlementMark
                          platform="whatsapp"
                          enabled={organization.platformEntitlements.whatsappEnabled}
                        />
                        <PlatformEntitlementMark
                          platform="facebook"
                          enabled={organization.platformEntitlements.facebookEnabled}
                        />
                        <PlatformEntitlementMark
                          platform="instagram"
                          enabled={organization.platformEntitlements.instagramEnabled}
                        />
                      </span>
                    </td>
                    <td className={superAdminTableBodyCellClass}>
                      <LtrValue>{new Date(organization.createdAt).toLocaleDateString(i18n.language)}</LtrValue>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <SuperAdminCursorPaginationBar
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            hasNextPage={nextCursor !== null}
            isFirstPage={isFirstPage}
            onNextPage={onNextPage}
            onFirstPage={onFirstPage}
            pageSizeLabel={t("superAdmin.pagination.pageSize")}
            nextLabel={t("superAdmin.organizations.next")}
            firstLabel={t("superAdmin.pagination.first")}
            showingCountLabel={t("superAdmin.pagination.showingCount")}
            itemCount={items.length}
            testIdPrefix="super-admin-org"
          />
        </div>
      ) : null}
    </div>
  );
}
